import { of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';

import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { Logout } from '../../../auth/auth.actions';
import { selectLoggedUser } from '../../../auth/auth.selectors';
import { ForceHideSpinner } from '../../../loader/loader.actions';
import { NotificationService } from '../../../notifications/notification.service';
import { Role } from '../role.enum';
import { UserService } from '../user.service';
import {
    CancellationRequest, CancellationRequestFail, CancellationRequestSuccess, CreateUser,
    CreateUserFail, CreateUserSuccess, DeleteUser, DeleteUserFail, DeleteUserSuccess,
    DownloadDocuments, DownloadDocumentsFail, DownloadDocumentsSuccess, LoadUsersByCompanyId,
    LoadUsersFail, LoadUsersSuccess, UpdateUser, UpdateUserFail, UpdateUserSuccess, UsersActionTypes
} from './user.actions';

@Injectable()
export class UsersEffects {

    constructor(
        private service: UserService,
        private actions$: Actions,
        private notificationService: NotificationService,
        private translateService: TranslateService,
        private location: Location,
        private router: Router,
        private store: Store) {
    }

    @Effect({ dispatch: false })
    genericFail$ = this.actions$.pipe(
        ofType<Action & { payload: string }>(
            UsersActionTypes.LoadUsersFail,
            UsersActionTypes.UpdateUserFail,
            UsersActionTypes.CreateUserFail,
            UsersActionTypes.DeleteUserFail,
            UsersActionTypes.CancellationRequestFail,
            UsersActionTypes.DownloadDocumentstFail,
        ),
        tap(action => this.notificationService.error(action.payload))
    );

    @Effect()
    loadUsers$ = this.actions$.pipe(
        ofType(UsersActionTypes.LoadUsers),
        mergeMap(_ =>
            this.service.getAll().pipe(
                map(items => (new LoadUsersSuccess(items))),
                catchError(err => of(new LoadUsersFail(err)))
            )
        )
    );

    @Effect()
    updateUser$ = this.actions$.pipe(
        ofType<UpdateUser>(UsersActionTypes.UpdateUser),
        mergeMap((action) =>
            this.service.put(action.user.id, action.user).pipe(
                map(result => (new UpdateUserSuccess(result))),
                catchError(err => of(new UpdateUserFail(err)))
            )
        )
    );

    @Effect()
    deleteUser$ = this.actions$.pipe(
        ofType<DeleteUser>(UsersActionTypes.DeleteUser),
        mergeMap((action) =>
            this.service.delete(action.user.id).pipe(
                map(result => (new DeleteUserSuccess(action.user))),
                catchError(err => of(new DeleteUserFail(err)))
            )
        )
    );

    @Effect()
    createUser$ = this.actions$.pipe(
        ofType<CreateUser>(UsersActionTypes.CreateUser),
        mergeMap((action) =>
            this.service.post(action.user).pipe(
                map(result => (new CreateUserSuccess(result))),
                catchError(err => of(new CreateUserFail(err)))
            )
        )
    );

    @Effect({ dispatch: false })
    updateUserSuccess$ = this.actions$.pipe(
        ofType(UsersActionTypes.UpdateUserSuccess),
        concatMap((action: UpdateUserSuccess) =>
        of(action).pipe(
            withLatestFrom(
                this.store.select(selectLoggedUser)),
            )
        ),
        tap(([action, loggedUser]) => {
            this.notificationService.success(this.translateService.instant('Utente modificato con successo'));
            if (!(loggedUser.role === Role.Employee || loggedUser.role === Role.Freelance)){
                this.location.back();
            }
        })
    );

    @Effect({ dispatch: false })
    deleteUserSuccess$ = this.actions$.pipe(
        ofType(UsersActionTypes.DeleteUserSuccess),
        tap((action: DeleteUserSuccess) => {
            this.notificationService.success(this.translateService.instant('Utente eliminato con successo'));
            this.location.back();
        })
    );

    @Effect({ dispatch: false })
    createUserSuccess$ = this.actions$.pipe(
        ofType(UsersActionTypes.CreateUserSuccess),
        tap((action: CreateUserSuccess) => {
            this.notificationService.success(this.translateService.instant('Utente creato con successo'));
            this.location.back();
        })
    );

    @Effect()
    loadUsersByCompanyId$ = this.actions$.pipe(
        ofType<LoadUsersByCompanyId>(UsersActionTypes.LoadUsersByCompanyId),
        mergeMap((action) =>
            this.service.getByCompanyId(action.companyId).pipe(
                map(items => {
                    this.store.dispatch(new ForceHideSpinner());
                    return new LoadUsersSuccess(items);
                }),
                catchError(err => of(new LoadUsersFail(err)))
            )
        )
    );

    @Effect()
    cancellationRequest$ = this.actions$.pipe(
      ofType<CancellationRequest>(UsersActionTypes.CancellationRequest),
      mergeMap((action) =>
          this.service.cancellationRequest(action.id).pipe(
            map(result => {
              // se il ruolo è employee o freelance può essere utile aggiornare l'utente loggato?
              // this.store.dispatch(new GetLoggedUserSuccess(result));
              return new CancellationRequestSuccess(result);
            } ),
            catchError(err => of(new CancellationRequestFail(err)))
          )
        )
    );

    @Effect()
    downloadDocuments$ = this.actions$.pipe(
      ofType<DownloadDocuments>(UsersActionTypes.DownloadDocuments),
      mergeMap((action) =>
          this.service.downloadDocuments(action.id).pipe(
            map(result => {
              return new DownloadDocumentsSuccess(result);
            } ),
            catchError(err => of(new DownloadDocumentsFail(err)))
          )
        )
    );
    @Effect({ dispatch: false })
    cancellationRequestSuccess$ = this.actions$.pipe(
      ofType(UsersActionTypes.CancellationRequestSuccess),
      tap((action: CancellationRequestSuccess) => {
        this.notificationService.success(this.translateService.instant('Operazione eseguita con successo'));
        if (action.user.role === Role.Freelance){
            this.store.dispatch( new Logout());
        }
    })
    );
}
